import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import { CmsEntities } from 'client/data/models/cms';
import { EditorialMap } from 'client/tracking/maps/editorial';
import { EngineTypeQuizMap } from 'client/tracking/maps/common/engine-type-quiz';
import { TrackingHandler } from 'client/tracking/handler';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { getCanonical } from 'site-modules/shared/utils/seo-helper';
import { scrollToHash } from 'client/utils/scroll';
import { isEvArticle } from 'site-modules/editorial/utils/ev';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { AnchorNavSticky } from 'site-modules/shared/components/anchor-nav-sticky/anchor-nav-sticky';
import { ArticleHeroPhoto } from 'site-modules/shared/components/editorial/article-hero-photo/article-hero-photo';
import { ResearchEntry } from 'site-modules/editorial/components/type/one-column/research-entry/research-entry';
import { ArticleReadyForEv } from 'site-modules/editorial/components/article-ready-for-ev/article-ready-for-ev';
import { ArticleTreehousePromotion } from 'site-modules/editorial/components/article-treehouse-promotion/article-treehouse-promotion';
import { ArticleEvToolLink } from 'site-modules/editorial/components/article-ev-tool-link/article-ev-tool-link';
import { ArticleMetadata } from 'site-modules/shared/components/editorial/article-metadata/article-metadata';
import { EngineTypeQuiz } from 'site-modules/shared/components/engine-type-quiz/engine-type-quiz';

import './one-column-template.scss';

function OneColumnTemplateUI({
  content = DEFAULT_CONTENT,
  allAuthors = {
    author: DEFAULT_CONTENT,
    coAuthor: DEFAULT_CONTENT,
    editor: DEFAULT_CONTENT,
  },
  pathname = '',
  slug = '',
  articleId = '',
  isMobile = false,
}) {
  useEffect(() => {
    TrackingHandler.useMap({ ...EditorialMap, ...EngineTypeQuizMap });
    scrollToHash(window.location.hash);
  }, []);

  const { title, subtitle, summary, categories } = content;
  const canonical = getCanonical(content);
  const isEvArticlePage = isEvArticle({ slug, categories });

  const heroPhoto = content.child('hero-content').links()[0];

  // anchor nav
  const subnavEntry = content.child('subnav');
  const navTitle = subnavEntry.metadata('subnavTitle').value();
  const navList = subnavEntry.links().map(({ href, title: name }) => ({
    name,
    hash: href.replace('#', ''),
  }));

  const entries = content.child('main').children();
  const entriesProps = {
    title,
    canonical,
    contentId: content.id,
    entryPath: ['main'],
    pageTitle: title,
    pathname,
    slug,
  };

  const readyForEvArticleStyles = {
    headingStyles: {
      fontSize: '1.25rem',
      letterSpacing: '0',
      lineHeight: '1.5rem',
    },
    wrapperStyles: {
      paddingTop: '1.5rem',
      paddingBottom: '1.5rem',
    },
  };

  return (
    <main id="main-content" tabIndex={-1} role="main" data-ad-zone={articleId}>
      <figure className="one-column-template hero">{!!heroPhoto && <ArticleHeroPhoto photo={heroPhoto} />}</figure>
      <article className="one-column-template content">
        <header className="one-column-content mx-1_5 mx-md-auto text-center bg-white header">
          <ScrollElement id="article-top" />
          <h1 className="heading-1 pt-1_5 mb-0_75 fw-bolder">{title}</h1>
          <h2 className="heading-2 text-cool-gray-40 mb-0_75 fw-medium">{subtitle}</h2>
          {summary && (
            <Fragment>
              <ContentFragment componentToUse="p" classes="summary mb-0_75 text-cool-gray-40 size-20 hidden-lg-down">
                {summary}
              </ContentFragment>
              <ContentFragment componentToUse="p" classes="summary mb-0_75 text-cool-gray-40 size-16 hidden-lg-up">
                {summary}
              </ContentFragment>
            </Fragment>
          )}
          <ArticleMetadata
            className="pb-2 justify-content-center"
            listClassName={classnames({ 'justify-content-center': isMobile })}
            content={content}
            allAuthors={allAuthors}
            creativeId="edm-entry-article-metadata"
            isMobile={isMobile}
          />
          {!!navList?.length && (
            <AnchorNavSticky
              className="mx-auto article-anchor-nav pb-1 mt-0"
              navList={navList}
              isMobile={isMobile}
              title={navTitle}
              hasSeparator={false}
              itemClassName="medium py-0_75 ps-0_75"
              linkClassName="py-0_25 px-1"
            />
          )}
        </header>

        {entries.map((entry, index) => {
          const key = `main-${entry.id}-${index}`;
          return (
            <Fragment key={key}>
              <ResearchEntry entry={entry} isMobile={isMobile} entriesProps={entriesProps} useMxMdAuto />
              {!index && (
                <section className="one-column-content mx-md-auto">
                  <ArticleTreehousePromotion
                    className="mb-3_5"
                    categories={content.categories}
                    slug={slug}
                    fallback={
                      <ArticleReadyForEv
                        categories={content.categories}
                        slug={slug}
                        className="mb-3_5"
                        headingStyles={readyForEvArticleStyles.headingStyles}
                        wrapperStyles={readyForEvArticleStyles.wrapperStyles}
                      />
                    }
                  />
                </section>
              )}
              {index === 1 && (
                <section className="one-column-content mx-md-auto">
                  <ArticleEvToolLink
                    categories={content.categories}
                    slug={slug}
                    className="mb-2 px-0_5 px-md-0"
                    isMobile={isMobile}
                  />
                </section>
              )}
            </Fragment>
          );
        })}
        {isEvArticlePage && (
          <div className="one-column-content mx-md-auto my-3_5">
            <EngineTypeQuiz />
          </div>
        )}
      </article>
    </main>
  );
}

OneColumnTemplateUI.propTypes = {
  content: CmsEntities.Content,
  slug: PropTypes.string,
  articleId: PropTypes.string,
  allAuthors: PropTypes.shape({
    author: CmsEntities.Content,
    coAuthor: CmsEntities.Content,
    editor: CmsEntities.Content,
  }),
  pathname: PropTypes.string,
  isMobile: PropTypes.bool,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  pathname: get(state, 'pageContext.location.pathname'),
});

export const OneColumnTemplate = connect(mapStateToProps)(OneColumnTemplateUI);
